/**
 * Notifica locale ricorrente giornaliera per il feedback obiettivo.
 * Si attiva all'orario preferito (default 08:00) e mostra un promemoria
 * per aprire la scheda "Obiettivi" e vedere il verdetto della notte.
 *
 * Design:
 *  - id 500 (distinto da 100 sveglia) per evitare conflitti
 *  - `every: "day"` per programmazione ricorrente nativa
 *  - Silent fallback su web (no-op)
 */

import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const GOAL_MORNING_ID = 500;

function isNative() {
  return Capacitor.getPlatform() !== "web";
}

async function ensurePermission() {
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status.display === "granted") return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === "granted";
  } catch {
    return false;
  }
}

/**
 * Pianifica la notifica ricorrente. Se già presente, la sovrascrive.
 * Ritorna {scheduled, reason?}.
 */
export async function scheduleGoalMorningNotification({
  hour = 8,
  minute = 0,
  title = "Il tuo sonno di stanotte",
  body = "Apri SmartDream per vedere come è andata rispetto al tuo obiettivo",
} = {}) {
  if (!isNative()) return { scheduled: false, reason: "not_native" };
  const ok = await ensurePermission();
  if (!ok) return { scheduled: false, reason: "no_permission" };

  const now = new Date();
  const at = new Date(now);
  at.setHours(hour, minute, 0, 0);
  if (at.getTime() <= now.getTime()) at.setDate(at.getDate() + 1);

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: GOAL_MORNING_ID,
          title,
          body,
          schedule: {
            at,
            every: "day",
            allowWhileIdle: true,
          },
          extra: { source: "goal_morning" },
        },
      ],
    });
    return { scheduled: true, at };
  } catch (e) {
    return { scheduled: false, reason: "schedule_error", error: String(e) };
  }
}

export async function cancelGoalMorningNotification() {
  if (!isNative()) return;
  try {
    await LocalNotifications.cancel({
      notifications: [{ id: GOAL_MORNING_ID }],
    });
  } catch {
    /* swallow */
  }
}
